import React from 'react'
import { Link } from 'react-router-dom'
import { ApiError, apiGet, apiPost } from '../../lib/api'
import { SANDBOX_QUOTA, SANDBOX_SEND, USER_ME } from '../../lib/endpoints'
import { loadAuth } from '../../lib/authStorage'
import type { UserProfile } from '../../types/api'

const BUSINESS_TYPES = [
  { value: 'SALON', label: 'Hair salon' },
  { value: 'DOG_GROOMER', label: 'Dog groomer' },
  { value: 'DENTIST', label: 'Dental practice' },
  { value: 'PERSONAL_TRAINER', label: 'Personal trainer' },
  { value: 'OTHER', label: 'Other' },
] as const

const LAST_VISIT_OPTIONS = [
  { value: 30, label: '30 days ago' },
  { value: 45, label: '45 days ago' },
  { value: 60, label: '60 days ago' },
  { value: 90, label: '90 days ago' },
]

type FormState = {
  businessName: string
  businessType: (typeof BUSINESS_TYPES)[number]['value']
  brandVoice: string
  customerEmail: string
  lastVisitDays: number
}

const DEFAULT_FORM: FormState = {
  businessName: "Bella's Hair Studio",
  businessType: 'SALON',
  brandVoice: 'warm, friendly, professional',
  customerEmail: '',
  lastVisitDays: 45,
}

type SandboxSendResponse = {
  remainingToday?: number
}

const DAILY_LIMIT = 3

export default function SandboxForm() {
  const [form, setForm] = React.useState<FormState>(DEFAULT_FORM)
  const [signedIn, setSignedIn] = React.useState<boolean>(() => !!loadAuth()?.accessToken)
  const [submitting, setSubmitting] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [success, setSuccess] = React.useState<{ remaining?: number } | null>(null)
  const [remainingToday, setRemainingToday] = React.useState<number | null>(null)

  React.useEffect(() => {
    if (!signedIn) return
    let cancelled = false
    apiGet<UserProfile>(USER_ME)
      .then(profile => {
        if (cancelled || !profile?.email) return
        setForm(prev => (prev.customerEmail ? prev : { ...prev, customerEmail: profile.email ?? '' }))
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [signedIn])

  React.useEffect(() => {
    if (!signedIn) { setRemainingToday(null); return }
    let cancelled = false
    apiGet<SandboxSendResponse>(SANDBOX_QUOTA)
      .then(res => { if (!cancelled && res?.remainingToday !== undefined) setRemainingToday(res.remainingToday) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [signedIn])

  React.useEffect(() => {
    const onFocus = () => setSignedIn(!!loadAuth()?.accessToken)
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [])

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm(prev => ({ ...prev, [key]: value }))

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!signedIn) return
    setSubmitting(true)
    setError(null)
    try {
      const res = await apiPost<SandboxSendResponse, FormState>(SANDBOX_SEND, form)
      if (res?.remainingToday !== undefined) setRemainingToday(res.remainingToday)
      setSuccess({ remaining: res?.remainingToday })
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 429) {
          setRemainingToday(0)
          setError("You've used all 3 test nudges for today. Come back tomorrow.")
        } else {
          setError(err.message || 'Could not send. Please try again.')
        }
      } else {
        setError('Could not send. Please try again.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const reset = () => {
    setSuccess(null)
    setError(null)
  }

  return (
    <div className="relative group">
      {/* Live demo badge — flat black pill */}
      <div
        className="sandbox-badge absolute -top-3 right-6 z-10 flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold font-dm-mono uppercase tracking-wider"
        style={{
          backgroundColor: '#111',
          color: '#FBE6D4',
          border: '2px solid #111',
          borderRadius: 999,
        }}
      >
        <span
          className="sandbox-badge-dot inline-block w-1.5 h-1.5 rounded-full"
          style={{ backgroundColor: 'var(--accent)' }}
        />
        Live demo — try it now
      </div>

      {/* Card interior — flat white, no border (outer wrapper in HeroSection supplies the border) */}
      <div
        className="sandbox-card relative overflow-hidden"
        style={{
          backgroundColor: '#fff',
          borderRadius: 16,
        }}
      >
        <div className="p-6">
        {success ? (
          <SuccessPanel email={form.customerEmail} remaining={success.remaining} onReset={reset} />
        ) : (
          <>
            <div className="flex items-start gap-3 mb-5">
              <div
                className="flex-shrink-0 w-10 h-10 flex items-center justify-center mt-0.5"
                style={{
                  backgroundColor: '#111',
                  borderRadius: 10,
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 2L11 13" /><path d="M22 2L15 22l-4-9-9-4 20-7z" />
                </svg>
              </div>
              <div>
                <h3 className="font-extrabold text-lg leading-tight tracking-tight" style={{ color: '#111', letterSpacing: '-0.015em' }}>
                  Try it live
                </h3>
                <p className="text-xs mt-0.5" style={{ color: '#5a4736' }}>
                  We&rsquo;ll send a real AI nudge to your inbox.
                </p>
              </div>
            </div>

            <form
              onSubmit={handleSubmit}
              className={`space-y-2.5 ${signedIn ? '' : 'pointer-events-none select-none'}`}
              aria-disabled={!signedIn}
            >
              <Field label="Business name">
                <input
                  type="text"
                  value={form.businessName}
                  onChange={e => update('businessName', e.target.value)}
                  className="sandbox-input"
                  maxLength={80}
                  required
                />
              </Field>

              <div className="grid grid-cols-2 gap-2.5">
                <Field label="Business type">
                  <div className="sandbox-select-wrap">
                    <select
                      value={form.businessType}
                      onChange={e => update('businessType', e.target.value as FormState['businessType'])}
                      className="sandbox-input sandbox-select"
                    >
                      {BUSINESS_TYPES.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    <svg className="sandbox-select-arrow" width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M2.5 4.5L6 8l3.5-3.5" stroke="#111" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </Field>
                <Field label="Last visit">
                  <div className="sandbox-select-wrap">
                    <select
                      value={form.lastVisitDays}
                      onChange={e => update('lastVisitDays', Number(e.target.value))}
                      className="sandbox-input sandbox-select"
                    >
                      {LAST_VISIT_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    <svg className="sandbox-select-arrow" width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M2.5 4.5L6 8l3.5-3.5" stroke="#111" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </Field>
              </div>

              <Field label="Brand voice">
                <input
                  type="text"
                  value={form.brandVoice}
                  onChange={e => update('brandVoice', e.target.value)}
                  className="sandbox-input"
                  maxLength={120}
                  placeholder="warm, friendly, professional"
                />
              </Field>

              <Field label="Send to (your email)">
                <div className="sandbox-email-wrap">
                  <svg className="sandbox-email-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5a4736" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                  </svg>
                  <input
                    type="email"
                    value={form.customerEmail}
                    readOnly
                    className="sandbox-input sandbox-input-icon sandbox-input-readonly"
                    placeholder={signedIn ? 'loading…' : 'sign in to use your email'}
                    required={signedIn}
                  />
                  {signedIn && (
                    <svg className="sandbox-lock-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#5a4736" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                    </svg>
                  )}
                </div>
              </Field>

              {error && (
                <p
                  className="text-xs px-3 py-2 font-medium"
                  style={{
                    color: '#111',
                    backgroundColor: '#FFD9D9',
                    border: '1.5px solid #111',
                    borderRadius: 8,
                  }}
                >
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={!signedIn || submitting}
                className="sandbox-submit w-full mt-1 px-4 py-3 text-sm font-bold transition-transform"
                style={{
                  backgroundColor: 'var(--accent)',
                  color: '#fff',
                  border: '2px solid #111',
                  borderRadius: 12,
                  boxShadow: '3px 3px 0 #111',
                }}
              >
                <span className="flex items-center justify-center gap-2">
                  {submitting ? (
                    <>
                      <svg className="sandbox-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>
                      Sending…
                    </>
                  ) : (
                    <>Send me a nudge <span aria-hidden>→</span></>
                  )}
                </span>
              </button>

              <QuotaIndicator remaining={remainingToday} limit={DAILY_LIMIT} signedIn={signedIn} />
            </form>
          </>
        )}
        </div>

        {/* Logged-out hover overlay */}
        {!signedIn && (
          <div
            className="sandbox-overlay absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity duration-200 pointer-events-none group-hover:pointer-events-auto focus-within:pointer-events-auto"
            style={{ borderRadius: 16 }}
          >
            <div className="text-center px-6">
              <div
                className="mx-auto w-12 h-12 flex items-center justify-center mb-3"
                style={{
                  backgroundColor: '#111',
                  border: '2px solid #111',
                  borderRadius: 12,
                  boxShadow: '3px 3px 0 var(--accent)',
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FBE6D4" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              </div>
              <p className="font-extrabold text-xl mb-1 tracking-tight" style={{ color: '#111', letterSpacing: '-0.015em' }}>
                Sign in to try it live
              </p>
              <p className="text-sm mb-5" style={{ color: '#3a2e26' }}>
                We&rsquo;ll send a real AI nudge to your inbox.
              </p>
              <div className="flex items-center justify-center gap-2.5">
                <Link
                  to="/signup"
                  className="sandbox-overlay-cta px-5 py-2.5 text-sm font-bold transition-transform"
                  style={{
                    backgroundColor: 'var(--accent)',
                    color: '#fff',
                    border: '2px solid #111',
                    borderRadius: 999,
                    boxShadow: '3px 3px 0 #111',
                    textDecoration: 'none',
                  }}
                >
                  Sign up free
                </Link>
                <Link
                  to="/signin"
                  className="sandbox-overlay-cta px-5 py-2.5 text-sm font-bold transition-transform"
                  style={{
                    backgroundColor: '#fff',
                    color: '#111',
                    border: '2px solid #111',
                    borderRadius: 999,
                    boxShadow: '3px 3px 0 #111',
                    textDecoration: 'none',
                  }}
                >
                  Sign in
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        /* Inputs — flat, bold black border */
        .sandbox-input {
          width: 100%;
          padding: 10px 12px;
          font-size: 0.8125rem;
          line-height: 1.35;
          font-weight: 500;
          color: #111;
          background-color: #fff;
          border: 1.5px solid #111;
          border-radius: 10px;
          outline: none;
          transition: box-shadow 0.15s, background-color 0.15s;
        }
        .sandbox-input::placeholder { color: #5a4736; }
        .sandbox-input:focus {
          box-shadow: 3px 3px 0 #111;
          background-color: #fff;
        }

        /* Custom select wrapper */
        .sandbox-select-wrap { position: relative; }
        .sandbox-select {
          appearance: none;
          -webkit-appearance: none;
          padding-right: 32px;
          cursor: pointer;
        }
        .sandbox-select-arrow {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          pointer-events: none;
        }

        /* Email input with icon */
        .sandbox-email-wrap { position: relative; }
        .sandbox-email-icon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          pointer-events: none;
        }
        .sandbox-lock-icon {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          pointer-events: none;
        }
        .sandbox-input-icon { padding-left: 32px; }
        .sandbox-input-readonly {
          cursor: default;
          background-color: #FAF5EC;
        }
        .sandbox-input-readonly:focus {
          box-shadow: none;
          background-color: #FAF5EC;
        }

        /* Submit button */
        .sandbox-submit:hover:not(:disabled) {
          transform: translate(-2px, -2px);
          box-shadow: 5px 5px 0 #111 !important;
        }
        .sandbox-submit:active:not(:disabled) {
          transform: translate(1px, 1px);
          box-shadow: 1px 1px 0 #111 !important;
        }
        .sandbox-submit:disabled {
          opacity: 0.55;
          cursor: not-allowed;
          box-shadow: 3px 3px 0 #111 !important;
        }

        /* Overlay CTAs */
        .sandbox-overlay-cta:hover {
          transform: translate(-2px, -2px);
          box-shadow: 5px 5px 0 #111 !important;
        }

        @keyframes sandbox-spin { to { transform: rotate(360deg); } }
        .sandbox-spin { animation: sandbox-spin 0.8s linear infinite; }

        /* Overlay backdrop */
        .sandbox-overlay {
          background: rgba(251, 230, 212, 0.85);
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(6px);
        }

        /* Badge dot pulse */
        @keyframes sandbox-badge-blink {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(0.75); }
        }
        .sandbox-badge-dot {
          animation: sandbox-badge-blink 1.4s ease-in-out infinite;
        }

        @media (prefers-reduced-motion: reduce) {
          .sandbox-badge-dot,
          .sandbox-spin {
            animation: none !important;
          }
        }
      `}</style>
    </div>
  )
}

function QuotaIndicator({ remaining, limit, signedIn }: { remaining: number | null; limit: number; signedIn: boolean }) {
  if (!signedIn) {
    return (
      <div className="flex items-center justify-center gap-1.5 pt-1">
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#5a4736" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
        <p className="text-[11px] font-dm-mono font-semibold" style={{ color: '#5a4736' }}>{limit} free test nudges per day</p>
      </div>
    )
  }

  const used = remaining === null ? 0 : limit - remaining
  const dotColor = (i: number) => {
    if (remaining === null) return '#d4cabc'
    return i < used ? 'var(--accent)' : '#d4cabc'
  }
  const label = remaining === null
    ? 'Loading…'
    : remaining === 0
      ? 'No nudges left today'
      : `${remaining} of ${limit} nudges left today`
  const labelColor = remaining === 0 ? '#c4361a' : remaining === 1 ? 'var(--accent)' : '#5a4736'

  return (
    <div className="flex items-center justify-center gap-2 pt-1">
      <div className="flex items-center gap-1">
        {Array.from({ length: limit }).map((_, i) => (
          <span
            key={i}
            className="inline-block w-2 h-2 rounded-full transition-colors duration-300"
            style={{ backgroundColor: dotColor(i) }}
          />
        ))}
      </div>
      <p className="text-[11px] font-dm-mono font-semibold transition-colors duration-300" style={{ color: labelColor }}>{label}</p>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span
        className="block text-[10.5px] font-bold font-dm-mono uppercase tracking-widest mb-1.5"
        style={{ color: '#111' }}
      >
        {label}
      </span>
      {children}
    </label>
  )
}

function SuccessPanel({
  email,
  remaining,
  onReset,
}: {
  email: string
  remaining?: number
  onReset: () => void
}) {
  return (
    <div className="py-6 text-center">
      <div
        className="mx-auto w-14 h-14 flex items-center justify-center mb-4"
        style={{
          backgroundColor: '#E7F0E3',
          border: '2px solid #111',
          borderRadius: 14,
          boxShadow: '3px 3px 0 #111',
        }}
      >
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth={3}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h3 className="font-extrabold text-xl mb-1 tracking-tight" style={{ color: '#111', letterSpacing: '-0.015em' }}>
        Nudge sent
      </h3>
      <p className="text-sm mb-4" style={{ color: '#3a2e26' }}>
        Check <span style={{ color: '#111', fontWeight: 700 }}>{email}</span> — it should arrive in a few seconds.
      </p>
      {typeof remaining === 'number' && (
        <p className="text-[11px] font-dm-mono font-semibold mb-4" style={{ color: '#5a4736' }}>
          {remaining} of 3 test nudges remaining today
        </p>
      )}
      <button
        type="button"
        onClick={onReset}
        className="sandbox-success-cta px-5 py-2.5 text-sm font-bold transition-transform"
        style={{
          backgroundColor: '#fff',
          color: '#111',
          border: '2px solid #111',
          borderRadius: 999,
          boxShadow: '3px 3px 0 #111',
        }}
      >
        Try another
      </button>
      <style>{`
        .sandbox-success-cta:hover { transform: translate(-2px, -2px); box-shadow: 5px 5px 0 #111 !important; }
      `}</style>
    </div>
  )
}
